import {
  useState,
  FunctionComponent,
  SVGProps,
  useEffect,
  useCallback,
} from "react";
import { Modal } from "@mantine/core";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { Button } from "@mantine/core";
import type { CryptocurrencyPayment } from "@/types/payment";
import useDepositInfo from "@/hooks/payment/useDepositInfo";
import { LoadingIcon } from "../common/icons";
import useVerifyClaim from "@/hooks/useVerifyClaim";
import { useBalance } from "wagmi";
import mixpanel from "mixpanel-browser";
import BigNumber from "bignumber.js";
import { amountDesc } from "@/utils/format";

import type { FC } from "react";
import useAccount from "@/hooks/useAccount";

export const PaymentIcon: FunctionComponent<SVGProps<SVGSVGElement>> = (
  props
) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="#585858"
      {...props}
    >
      <path d="M11.4995 14.3627H5.49945C5.38243 14.3627 5.26747 14.3319 5.16613 14.2733C5.06479 14.2148 4.98063 14.1307 4.92212 14.0293L1.92212 8.83333C1.86374 8.73194 1.83301 8.61699 1.83301 8.5C1.83301 8.383 1.86374 8.26805 1.92212 8.16666L4.92212 2.97066C4.98063 2.86932 5.06479 2.78516 5.16613 2.72665C5.26747 2.66814 5.38243 2.63733 5.49945 2.63733H11.4995C11.6165 2.63733 11.7314 2.66814 11.8328 2.72665C11.9341 2.78516 12.0183 2.86932 12.0768 2.97066L15.0768 8.16666C15.1352 8.26805 15.1659 8.383 15.1659 8.5C15.1659 8.61699 15.1352 8.73194 15.0768 8.83333L12.0768 14.0293C12.0183 14.1307 11.9341 14.2148 11.8328 14.2733C11.7314 14.3319 11.6165 14.3627 11.4995 14.3627Z" />
    </svg>
  );
};

const CryptocurrencyPaymentDialog: FC<{
  title: string;
  serviceId: string;
  opened: boolean;
  onClose: any;
  payments: CryptocurrencyPayment[];
  payBtnText?: string;
  onMint: any;
}> = ({ title, opened, onClose, payments, payBtnText, onMint }) => {
  const { address } = useAccount();
  const [selectPaymentIndex, setSelectPaymentIndex] = useState(-1);
  const payment = payments[selectPaymentIndex];
  const [minting, setMinting] = useState(false);

  const bridgeInfo = useDepositInfo(payment);
  const { changeForm, changeVerifyState } = useVerifyClaim("karat");
  const { data: balance } = useBalance({
    address,
    chainId: payment?.chain?.id,
    enabled: !!payment,
  });

  const { data: tokenBalance, error } = useBalance(
    !!payment?.mintFee?.token &&
      payment?.mintFee?.token.address != payment.tokenAddress
      ? {
          address,
          token: payment?.mintFee?.token?.address as any,
          chainId: payment?.mintFee?.token?.chainId,
        }
      : undefined
  );

  const onChoosePayment = useCallback(
    (index: number) => {
      setSelectPaymentIndex(index);
      const payment = payments[index];
      if (payment) {
        mixpanel.track("choose_crypto_payment", {
          type: payment.name,
        });
      }
    },
    [payments, setSelectPaymentIndex]
  );

  const splitMintFee =
    !payment?.enableOneClick &&
    !!payment?.mintFee &&
    payment?.mintFee?.token?.address !== payment?.tokenAddress;
  const gasFee = new BigNumber(payment?.gas || 0);
  const bridgeFee = new BigNumber(bridgeInfo?.info?.tokenEscrowAmount || 0);
  let mintFee = new BigNumber(payment?.mintFee?.tokenAmount || 0);
  if (payment?.enableOneClick) {
    mintFee = new BigNumber(bridgeInfo?.info?.tokenMintAmount || 0);
  }
  const totalNativeFee = gasFee
    .plus(bridgeFee)
    .plus(!splitMintFee ? mintFee : 0);

  const isMintFeeInsufficient = new BigNumber(
    payment?.mintFee?.token &&
    payment?.mintFee?.token?.address !== payment.tokenAddress
      ? tokenBalance?.formatted || 0
      : balance?.formatted || 0
  ).isLessThan(mintFee);
  const isInsufficient =
    isMintFeeInsufficient ||
    totalNativeFee.isGreaterThan(new BigNumber(balance?.formatted || 0));

  const onMintClick = useCallback(async () => {
    const payment = payments[selectPaymentIndex];
    if (minting || !payment) {
      return;
    }
    try {
      setMinting(true);
      await onMint(payment);
      onClose();
    } finally {
      setMinting(false);
    }
  }, [minting, onMint, payments, selectPaymentIndex, onClose]);

  useEffect(() => {
    if (bridgeInfo.info) {
      changeForm(bridgeInfo.info);
    } else {
      changeForm(null);
    }
    changeVerifyState(true);
  }, [bridgeInfo.info]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      size={495}
      radius={16}
    >
      <div
        className="absolute right-2 top-2 p-3 cursor-pointer z-10"
        onClick={onClose}
      >
        <AiOutlineCloseCircle
          className="fill-primary"
          size={26}
        ></AiOutlineCloseCircle>
      </div>
      <div className="relative p-5 font-exo">
        <div className="text-center text-[32px] text-correct font-semibold">
          {title}
        </div>
        <div className="mt-10">
          <div className="text-sm">
            How do you want to pay for transaction fees?
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {payments.map((payment, index) => {
              const isActive = selectPaymentIndex === index;
              return (
                <div
                  className={`flex flex-col items-center justify-center border border-[#E0E1E1] p-4 rounded-[4px] min-h-[100px] cursor-pointer ${
                    isActive
                      ? isInsufficient
                        ? "bg-[#F9F9F9]"
                        : "bg-primary text-white"
                      : ""
                  } `}
                  key={payment?.name}
                  onClick={() => {
                    onChoosePayment(index);
                  }}
                >
                  <div className="flex items-center">
                    <PaymentIcon
                      className={
                        isActive ? (isInsufficient ? "" : "fill-white") : ""
                      }
                    ></PaymentIcon>
                    <div className="text-sm ml-1 font-semibold">
                      {payment.name}
                    </div>
                  </div>
                  <div className="mt-1">
                    {payment.enableOneClick
                      ? payment.symbol
                      : payment.mintFee?.token?.symbol}
                  </div>
                  {isActive && isInsufficient && (
                    <div className="text-xs mt-2">Insufficient balance</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {payment ? (
          <div className="mt-4 py-4 px-6 bg-[#F9F9F9] rounded-lg flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-dark2">Mint fee</div>
              <div className="flex items-center">
                {amountDesc(mintFee.toNumber(), 4)}{" "}
                {payment?.mintFee?.token?.symbol || payment.symbol} ($5)
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-dark2">Gas on {payment.chain.name}</div>
              <div className="flex items-center">
                <div className="text-xs mr-1">Est.</div>
                {payment.gas} {payment.symbol}
              </div>
            </div>
            {payment.enableOneClick && (
              <div className="flex items-center justify-between">
                <div className="text-dark2">Karat bridging fee</div>
                {bridgeInfo.isLoading ? (
                  <div className="relative h-4">
                    <LoadingIcon className="w-4 h-4 absolute top-[1px] right-0"></LoadingIcon>
                  </div>
                ) : (
                  <div>
                    {bridgeFee.isEqualTo(0)
                      ? "--"
                      : `${bridgeFee.toFixed(4)} ${payment.symbol} ($2)`}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between font-semibold mt-2">
              <div className="text-dark1 ">Transaction summary</div>
              <div>
                {totalNativeFee.isGreaterThan(0)
                  ? `${totalNativeFee.toFixed(4)} ${payment.symbol} ${
                      splitMintFee
                        ? `+ ${amountDesc(mintFee.toNumber(), 4)} ${
                            payment?.mintFee?.token?.symbol || payment.symbol
                          }`
                        : ""
                    }`
                  : "--"}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 py-4 px-6 bg-[#F9F9F9] rounded-lg flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="text-dark2">Gas</div>
              <div>--</div>
            </div>
            <div className="flex items-center justify-between font-semibold">
              <div className="text-dark1 ">Transaction summary</div>
              <div>--</div>
            </div>
          </div>
        )}
        <div className="mt-10 flex flex-col justify-center items-center">
          <Button
            className="w-[149px]"
            size="md"
            radius="xl"
            disabled={!payment || bridgeInfo.isLoading || isInsufficient}
            onClick={onMintClick}
            loading={minting}
          >
            {payBtnText || "Pay & mint"}
          </Button>
          {payment && payment.nativeBridgeUrl && (
            <a
              href={payment.nativeBridgeUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 text-primary text-sm font-semibold action hover:text-primary"
            >
              Use {payment.name} Native Bridge
            </a>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CryptocurrencyPaymentDialog;
